import React, { useState, useCallback, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Layout, ConfigProvider, Spin, Button, Grid, Dropdown } from 'antd';
import { MenuOutlined, UserOutlined, LogoutOutlined } from '@ant-design/icons';
import 'antd/dist/reset.css';
import { AuthProvider, useAuth, hasPermission } from './contexts/AuthContext';
import Sidebar from './components/Sidebar';
import Karsilama from './pages/Karsilama';
import Anasayfa from './pages/Anasayfa';
import Malzemeler from './pages/Malzemeler';
import Urunler from './pages/Urunler';
import Uretimler from './pages/Uretimler';
import Satislar from './pages/Satislar';
import Alislar from './pages/Alislar';
import Cariler from './pages/Cariler';
import Musteriler from './pages/Musteriler';
import Odemeler from './pages/Odemeler';
import Tahsilatlar from './pages/Tahsilatlar';
import Virman from './pages/Virman';
import Kasalar from './pages/Kasalar';
// import Ajanda from './pages/Ajanda'; // şimdilik kapalı; ileride kullanıcı notları için
import Kullanicilar from './pages/Kullanicilar';
import SatisTakvimi from './pages/SatisTakvimi';
import Raporlama from './pages/Raporlama';
import ListeFiyatlari from './pages/ListeFiyatlari';
import LogKayitlari from './pages/LogKayitlari';
import GelirTipleri from './pages/GelirTipleri';
import Paketleme from './pages/Paketleme';

const { Content, Header } = Layout;

function ProtectedRoute({ path, element }) {
  const { user, rolePermissions } = useAuth();
  if (!hasPermission(user, path, rolePermissions)) {
    return <Navigate to="/" replace />;
  }
  return element;
}

function AppContent() {
  const { user, loading, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(true);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md; // < 768px
  const isDesktop = screens.lg; // ≥ 992px

  // Rota değiştiğinde sidebar otomatik kapansın (mobilde Antd onClick güvenilir değil)
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      setCollapsed(true);
    }
  }, [location.pathname]);
  // Content çerçeve boyutları: mobilde sıkı, desktop'ta rahat
  const contentMargin = isMobile ? 3 : isDesktop ? 20 : 12;
  const contentPadding = isMobile ? 12 : isDesktop ? 24 : 16;
  const contentRadius = isMobile ? 6 : 8;

  const handleSidebarMenuClick = useCallback(() => setCollapsed(true), []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) {
    return <Karsilama />;
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={collapsed} onMenuClick={handleSidebarMenuClick} />
      <Layout>
        <Header style={{
          background: '#fff',
          padding: '0 8px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 56,
          gap: 8,
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: 18, width: 40, height: 40, flexShrink: 0 }}
              aria-label={collapsed ? 'Menüyü aç' : 'Menüyü kapat'}
            />
            {!isMobile && (
              <span style={{
                color: '#999',
                fontSize: 13,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                EVDEYE Restoran Yonetim Sistemi
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            {isMobile ? (
              <Dropdown
                placement="bottomRight"
                menu={{
                  items: [
                    {
                      key: 'user',
                      label: (
                        <span style={{ color: '#666' }}>
                          {user.ad}
                          <span style={{
                            background: '#FFF3E6',
                            color: '#FF6B00',
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            marginLeft: 6,
                          }}>{user.rol?.toUpperCase()}</span>
                        </span>
                      ),
                      disabled: true,
                    },
                    { type: 'divider' },
                    {
                      key: 'logout',
                      label: 'Çıkış Yap',
                      icon: <LogoutOutlined />,
                      danger: true,
                      onClick: logout,
                    },
                  ],
                }}
              >
                <Button
                  type="text"
                  icon={<UserOutlined />}
                  style={{ width: 40, height: 40, fontSize: 18 }}
                  aria-label="Kullanıcı menüsü"
                />
              </Dropdown>
            ) : (
              <>
                <span style={{ color: '#666', fontSize: 13, whiteSpace: 'nowrap' }}>
                  {user.ad}
                  <span style={{
                    background: '#FFF3E6',
                    color: '#FF6B00',
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 600,
                    marginLeft: 4,
                  }}>{user.rol?.toUpperCase()}</span>
                </span>
                <span
                  onClick={logout}
                  style={{
                    color: '#ff4d4f',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Cikis Yap
                </span>
              </>
            )}
          </div>
        </Header>
        <Content style={{ margin: contentMargin, padding: contentPadding, background: '#fff', borderRadius: contentRadius }}>
          <Routes>
            <Route path="/" element={<Anasayfa />} />
            <Route path="/malzemeler" element={<ProtectedRoute path="/malzemeler" element={<Malzemeler />} />} />
            <Route path="/urunler" element={<ProtectedRoute path="/urunler" element={<Urunler />} />} />
            <Route path="/uretimler" element={<ProtectedRoute path="/uretimler" element={<Uretimler />} />} />
            <Route path="/satislar" element={<ProtectedRoute path="/satislar" element={<Satislar />} />} />
            <Route path="/alislar" element={<ProtectedRoute path="/alislar" element={<Alislar />} />} />
            <Route path="/odemeler" element={<ProtectedRoute path="/odemeler" element={<Odemeler />} />} />
            <Route path="/tahsilatlar" element={<ProtectedRoute path="/tahsilatlar" element={<Tahsilatlar />} />} />
            <Route path="/virman" element={<ProtectedRoute path="/virman" element={<Virman />} />} />
            <Route path="/kasalar" element={<ProtectedRoute path="/kasalar" element={<Kasalar />} />} />
            <Route path="/cariler" element={<ProtectedRoute path="/cariler" element={<Cariler />} />} />
            <Route path="/musteriler" element={<ProtectedRoute path="/musteriler" element={<Musteriler />} />} />
            {/* <Route path="/ajanda" element={<ProtectedRoute path="/ajanda" element={<Ajanda />} />} /> */}
            <Route path="/kullanicilar" element={<ProtectedRoute path="/kullanicilar" element={<Kullanicilar />} />} />
            <Route path="/satis-takvimi" element={<ProtectedRoute path="/satis-takvimi" element={<SatisTakvimi />} />} />
            <Route path="/paketleme" element={<ProtectedRoute path="/paketleme" element={<Paketleme />} />} />
            <Route path="/raporlama" element={<ProtectedRoute path="/raporlama" element={<Raporlama />} />} />
            <Route path="/ayarlar/liste-fiyatlari" element={<ProtectedRoute path="/ayarlar/liste-fiyatlari" element={<ListeFiyatlari />} />} />
            <Route path="/ayarlar/gelir-tipleri" element={<ProtectedRoute path="/ayarlar/gelir-tipleri" element={<GelirTipleri />} />} />
            <Route path="/log-kayitlari" element={<ProtectedRoute path="/log-kayitlari" element={<LogKayitlari />} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
}

function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#FF6B00',
          borderRadius: 8,
          fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
        },
      }}
    >
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
